import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { type CropperRef, type Coordinates } from 'react-advanced-cropper';
import auditService from '../../services/auditService';
import authService from '../../services/authService';
import showToast from '../../utils/toastUtils';
import { createImage, getCroppedImageFile } from '../../utils/auditReceiptImageUtils';
import { buildNonTicketReportUpdateRequest } from '../../features/audit/fixOcr/utils/nonTicketReportPageUtils';
import type { PerspectiveCropperHandle } from '../../types/perspectiveCropper';
import type { FreeCropHandle } from '../../types/freeCrop';
import type { StoreLocation } from '../../types/services/auth';
import { filterLocationsForReceiptReport } from '../../utils/editReceiptLocationUtils';
import { UseEditReceiptDialogLogicParams, UseEditReceiptDialogLogicResult } from '../../types/editRecieptDailog';


const clampRotationValue = (value: number) => Math.max(-180, Math.min(180, value));
export const useEditReceiptDialogLogic = ({
  open,
  queueId,
  storeId,
  reportType,
  formData,
  onClose,
  onSuccess,
}: UseEditReceiptDialogLogicParams): UseEditReceiptDialogLogicResult => {
  const [existingImageUrl, setExistingImageUrl] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [sourceFile, setSourceFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [locations, setLocations] = useState<StoreLocation[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [locationsResolved, setLocationsResolved] = useState(false);
  const [editMode, setEditMode] = useState<'tilt' | 'perspective' | 'free'>('free');
  const [imageMetadata, setImageMetadata] = useState<{ width: number; height: number } | null>(null);
  const [modeSwitching] = useState(false);
  const [perspectiveLoading, setPerspectiveLoading] = useState(false);
  const [perspectiveKey, setPerspectiveKey] = useState(0);
  const [horizontalTilt, setHorizontalTilt] = useState(0);
  const [verticalTilt, setVerticalTilt] = useState(0);
  const [rotation, setRotation] = useState<number>(0);
  const [freeCropPreviewBlob, setFreeCropPreviewBlob] = useState<Blob | null>(null);

  const cropperRef = useRef<CropperRef | null>(null);
  const coordinatesRef = useRef<Coordinates | null>(null);
  const perspectiveCropperRef = useRef<PerspectiveCropperHandle | null>(null);
  const freeCropRef = useRef<FreeCropHandle | null>(null);
  const navigate = useNavigate();
  const loadExistingImage = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const imageBlob = await auditService.getAuditImage(queueId);
      const inferredType = imageBlob.type || 'image/jpeg';
      const existingFile = new File([imageBlob], 'existing-receipt.jpg', { type: inferredType });
      const url = URL.createObjectURL(imageBlob);
      setExistingImageUrl(url);
      setPreviewUrl(url);
      setSourceFile(existingFile);
      setRotation(0);
      setEditMode('free');
      setPerspectiveLoading(false);
      setImageMetadata(null);
      coordinatesRef.current = null;
      setFreeCropPreviewBlob(null);
      freeCropRef.current?.reset();
      setLoading(false);
    } catch (err) {
      setLoading(false);
      setError('Failed to load existing receipt image. Please try again.');
      showToast.error('Failed to load existing receipt image');
    }
  }, [queueId]);

  useEffect(() => {
    if (open && queueId) {
      loadExistingImage();
    }

    return () => {
      // no-op
    };
  }, [open, queueId, loadExistingImage]);

  useEffect(() => {
    if (!open || !storeId) {
      setLocations([]);
      setSelectedLocationId('');
      setLocationsLoading(false);
      setLocationsResolved(false);
      return;
    }

    let cancelled = false;
    const existingLocationId = formData?.store_location_id || '';
    setLocations([]);
    setSelectedLocationId(existingLocationId);
    setLocationsLoading(true);
    setLocationsResolved(false);

    const loadLocations = async () => {
      try {
        const response = await authService.getStoreLocations(storeId);
        if (cancelled) return;

        const activeLocations = response.filter((location) => location.active !== false);
        const normalizedReportType = String(reportType || '').toLowerCase();
        const compatibleLocations = filterLocationsForReceiptReport(activeLocations, reportType);
        const existingLocationIsActive = compatibleLocations.some(
          (location) => location.id === existingLocationId,
        );
        setLocations(compatibleLocations);
        setSelectedLocationId(
          existingLocationIsActive
            ? existingLocationId
            : compatibleLocations.length === 1 ? compatibleLocations[0].id : '',
        );
        if (compatibleLocations.length === 0) {
          setError(
            normalizedReportType === 'ticket_balance'
              ? 'This store has no active counter location.'
              : 'This store has no active compatible location.',
          );
          setLocationsResolved(false);
        } else {
          setLocationsResolved(true);
        }
      } catch {
        if (!cancelled) {
          setLocations([]);
          setSelectedLocationId('');
          setError('Failed to load store locations. Please try again.');
        }
      } finally {
        if (!cancelled) {
          setLocationsLoading(false);
        }
      }
    };

    void loadLocations();
    return () => {
      cancelled = true;
    };
  }, [formData?.store_location_id, open, reportType, storeId]);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      if (existingImageUrl) URL.revokeObjectURL(existingImageUrl);
    };
  }, [previewUrl, existingImageUrl]);
  const refreshPreviewFromFile = useCallback((file: File) => {
    setSelectedFile(file);
    setSourceFile(file);
    setPreviewUrl((prev) => {
      if (prev) {
        URL.revokeObjectURL(prev);
      }
      return URL.createObjectURL(file);
    });
    coordinatesRef.current = null;
    setRotation(0);
    setImageMetadata(null);
    setPerspectiveKey((key) => key + 1);
    setHorizontalTilt(0);
    setVerticalTilt(0);
    setFreeCropPreviewBlob(null);
    freeCropRef.current?.reset();
  }, []);

  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file (JPEG, PNG, etc.)');
        showToast.error('Please select an image file (JPEG, PNG, etc.)');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        showToast.error('File size must be less than 10MB');
        return;
      }

      setError(null);
      refreshPreviewFromFile(file);
      setEditMode('free');
      setPerspectiveLoading(false);
    }
  }, [refreshPreviewFromFile]);

  const onCropperChange = useCallback((cropper: CropperRef) => {
    if (editMode !== 'tilt') {
      return;
    }
    coordinatesRef.current = cropper.getCoordinates();
  }, [editMode]);

  useEffect(() => {
    let isCancelled = false;

    if (!previewUrl || editMode !== 'tilt') {
      setImageMetadata(null);
      return;
    }

    createImage(previewUrl)
      .then((image) => {
        if (!isCancelled) {
          setImageMetadata({
            width: image.naturalWidth || image.width,
            height: image.naturalHeight || image.height,
          });
        }
      })
      .catch(() => {
        if (!isCancelled) {
          setImageMetadata(null);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [previewUrl, editMode, createImage]);

  useEffect(() => {
    if (editMode !== 'tilt') {
      return;
    }

    if (!previewUrl || !cropperRef.current || !imageMetadata) {
      return;
    }

    const timer = setTimeout(() => {
      const coords =
        coordinatesRef.current ?? {
          left: 0,
          top: 0,
          width: imageMetadata.width,
          height: imageMetadata.height,
        };

      cropperRef.current?.setCoordinates(coords);
    }, 240);

    return () => clearTimeout(timer);
  }, [previewUrl, imageMetadata, editMode, rotation]);

  useEffect(() => {
    if (editMode === 'perspective') {
      perspectiveCropperRef.current?.setRotation(rotation);
    }
  }, [editMode, rotation]);

  const retainCoordinates = useCallback(() => {
    if (cropperRef.current) {
      coordinatesRef.current = cropperRef.current.getCoordinates();
    }
  }, []);

  const updateRotation = useCallback(
    (value: number) => {
      retainCoordinates();
      setRotation(clampRotationValue(value));
    },
    [retainCoordinates],
  );

  const getPerspectiveCroppedImg = useCallback(async (): Promise<File> => {
    if (!sourceFile) {
      throw new Error('No file selected');
    }
    if (!perspectiveCropperRef.current) {
      throw new Error('Perspective editor is not ready yet');
    }

    const blob = await perspectiveCropperRef.current.done();
    return new File([blob], sourceFile.name, {
      type: sourceFile.type,
      lastModified: Date.now(),
    });
  }, [sourceFile]);

  const getFreeCroppedImg = useCallback(async (): Promise<File> => {
    if (!sourceFile) {
      throw new Error('No file selected');
    }
    if (!freeCropRef.current) {
      throw new Error('Free crop editor is not ready yet');
    }

    let blob = freeCropRef.current.getSavedImage();
    if (!blob) {
      blob = freeCropPreviewBlob;
    }
    if (!blob) {
      blob = await freeCropRef.current.done();
    }

    return new File([blob], sourceFile.name, {
      type: sourceFile.type,
      lastModified: Date.now(),
    });
  }, [sourceFile, freeCropPreviewBlob]);

  const onRotationChange = useCallback(
    (_: Event, value: number | number[]) => {
      const r = Array.isArray(value) ? value[0] : value;
      updateRotation(r);
      perspectiveCropperRef.current?.setRotation(r);
    },
    [updateRotation],
  );


  const handleFreeCropPreview = useCallback(async () => {
    if (!freeCropRef.current) {
      setError('Free crop editor is not ready yet');
      showToast.error('Free crop editor is not ready yet');
      return;
    }

    try {
      const blob = await freeCropRef.current.preview();
      setFreeCropPreviewBlob(blob);
      freeCropRef.current.showPreview();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to generate preview';
      setError(message);
      showToast.error(message);
    }
  }, []);

  const handleFreeCropEdit = useCallback(() => {
    if (freeCropRef.current) {
      freeCropRef.current.hidePreview();
      setFreeCropPreviewBlob(null);
    }
  }, []);

  const handleFreeCropRevert = useCallback(() => {
    if (freeCropRef.current) {
      freeCropRef.current.revertToInitial();
      setFreeCropPreviewBlob(null);
    }
  }, []);

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      const file = event.dataTransfer.files[0];
      if (file) {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.files = event.dataTransfer.files;
        input.onchange = (e) => handleFileSelect(e as any);
        input.click();
      }
    },
    [handleFileSelect],
  );

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
  }, []);

  const handleLocationChange = useCallback((locationId: string) => {
    setSelectedLocationId(locationId);
    setError(null);
  }, []);

  const hasInventoryPackFields = useCallback((value: any): boolean => {
    const lines = Array.isArray(value?.parsed_data?.final_json?.lines)
      ? value.parsed_data.final_json.lines
      : [];

    return lines.some((line: any) => (
      line?.required_pack_count !== undefined
      || Array.isArray(line?.selected_packs)
      || line?.selected_pack_id !== undefined
      || line?.pack_capacity !== undefined
      || line?.pack !== undefined
    ));
  }, []);

  const buildRejectRequestFromForm = useCallback((data: any) => {
    const safeData = data ?? {};
    let dateInMmDdYy = safeData.shift_date;
    if (safeData.shift_date && safeData.shift_date.includes('-')) {
      const [year, month, day] = safeData.shift_date.split('-');
      const yy = year.slice(-2);
      dateInMmDdYy = `${month}/${day}/${yy}`;
    }

    if (hasInventoryPackFields(safeData)) {
      const inventoryPayload = buildNonTicketReportUpdateRequest('vending_inventory', safeData, queueId);
      return {
        action: 'reject' as const,
        notes: safeData.notes || 'Rejected by user',
        confidence_score: Number(safeData.confidence_score || 0),
        location_key: inventoryPayload.location_key,
        shift_date: inventoryPayload.shift_date,
        shift_time: inventoryPayload.shift_time,
        report_date: inventoryPayload.report_date,
        report_time: inventoryPayload.report_time,
        receipt_audit_queue_id: queueId,
        parsed_data: inventoryPayload.parsed_data,
      };
    }

    if (safeData.shift_date && safeData.shift_start_time && safeData.shift_end_time) {
      const ticketDetails = Array.isArray(formData?.ticket_details) ? formData.ticket_details : [];
      return {
        action: 'reject' as const,
        notes: safeData.notes || 'Rejected by user',
        confidence_score: Number(safeData.confidence_score || 0),
        parsed_data: {
          final_json: {
            date: dateInMmDdYy,
            time: `${safeData.shift_start_time} TO ${safeData.shift_end_time}`,
            ticket_details: ticketDetails.map((item: any) => {
              return {
                game: item.game,
                name: item.name,
                pack: item.pack,
                range: item.range,
              };
            }),
            retailer: safeData.retailer_id,
          },
        },
      };
    }

    return {
      action: 'reject' as const,
      notes: safeData.notes || 'Rejected by user',
      confidence_score: Number(safeData.confidence_score || 0),
      parsed_data: safeData.parsed_data || {},
    };
  }, [formData?.ticket_details, hasInventoryPackFields, queueId]);

  const handleSubmit = useCallback(async () => {
    if (!locationsResolved) {
      setError('Store locations are still loading. Please try again.');
      return;
    }
    if (locations.length > 1 && !selectedLocationId) {
      setError('Please select a store location');
      showToast.error('Please select a store location');
      return;
    }

    try {
      setUploading(true);
      setError(null);

      const rejectRequest = buildRejectRequestFromForm(formData);
      const rejectResponse = await auditService.updateAuditStatus(queueId, rejectRequest);
      const originalUserId = rejectResponse?.user_id;

      if (!previewUrl) throw new Error('No image available to upload');

      let fileToUpload: File;
      if (editMode === 'perspective') {
        fileToUpload = await getPerspectiveCroppedImg();
      } else if (editMode === 'free') {
        fileToUpload = await getFreeCroppedImg();
      } else {
        fileToUpload = await getCroppedImageFile({
          previewUrl,
          selectedFile: selectedFile ?? sourceFile,
          rotation,
          coordinates: coordinatesRef.current,
        });
      }

      const response = await auditService.uploadReceipt(fileToUpload, storeId, {
        originalUserId,
        sourceQueueId: queueId,
        storeLocationId: selectedLocationId || null,
      });

      showToast.success('Receipt uploaded.');
      onSuccess(response);
      const redirectUrl = `/dashboard/audit${window.location.search}`;
      navigate(redirectUrl);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to update receipt';
      setError(errorMsg);
      showToast.error(errorMsg);
    } finally {
      setUploading(false);
    }
  }, [buildRejectRequestFromForm, editMode, formData, getFreeCroppedImg, getPerspectiveCroppedImg, locations.length, locationsResolved, navigate, onSuccess, previewUrl, queueId, rotation, selectedFile, selectedLocationId, sourceFile, storeId]);

  const handleClose = useCallback(() => {
    setSelectedFile(null);
    setSourceFile(null);
    setPreviewUrl('');
    setError(null);
    setLocations([]);
    setSelectedLocationId('');
    setLocationsLoading(false);
    setLocationsResolved(false);
    setUploading(false);
    setRotation(0);
    setEditMode('free');
    setPerspectiveLoading(false);
    setPerspectiveKey(0);
    setImageMetadata(null);
    coordinatesRef.current = null;
    perspectiveCropperRef.current?.backToCrop?.();
    freeCropRef.current?.reset();
    setFreeCropPreviewBlob(null);

    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    if (existingImageUrl) {
      URL.revokeObjectURL(existingImageUrl);
    }

    onClose();
  }, [existingImageUrl, onClose, previewUrl]);

  const handlePerspectiveState = useCallback((state: { loading?: boolean }) => {
    setPerspectiveLoading(Boolean(state?.loading));
  }, []);
  const onHorizontalTiltChange = useCallback((_: Event, value: number | number[]) => {
    const v = Array.isArray(value) ? value[0] : value;
    setHorizontalTilt(v);
    perspectiveCropperRef.current?.setHorizontalTilt(v);
  }, []);

  const onVerticalTiltChange = useCallback((_: Event, value: number | number[]) => {
    const v = Array.isArray(value) ? value[0] : value;
    setVerticalTilt(v);
    perspectiveCropperRef.current?.setVerticalTilt(v);
  }, []);

  return {
    existingImageUrl,
    selectedFile,
    sourceFile,
    previewUrl,
    loading,
    uploading,
    error,
    locations,
    selectedLocationId,
    locationsLoading,
    locationsResolved,
    editMode,
    imageMetadata,
    modeSwitching,
    perspectiveLoading,
    perspectiveKey,
    horizontalTilt,
    verticalTilt,
    rotation,
    freeCropPreviewBlob,
    cropperRef,
    perspectiveCropperRef,
    freeCropRef,
    onCropperChange,
    onRotationChange,
    onHorizontalTiltChange,
    onVerticalTiltChange,
    handleFileSelect,
    handleDrop,
    handleDragOver,
    handleLocationChange,
    handleFreeCropPreview,
    handleFreeCropEdit,
    handleFreeCropRevert,
    handlePerspectiveState,
    handleSubmit,
    handleClose,
    setError,
  };
};
